// src/pages/UserAdmin/AdminMeta.js — Dados da empresa Meta + criação de templates.
//
// Estrutura nova:
//   - Header no estilo das páginas de Conversas/Agentes (h4 800, mini-stats).
//   - Fonte Syne nos títulos (carregada via public/index.html).
//   - Duas abas: "Dados da empresa" (read-only) e "Criar templates" (form).
//   - Bloco "Seus Chips Meta" começa colapsado mostrando só 1 chip; botão
//     "Ver mais (N)" revela o restante quando há vários.
//
// ⚠️ Embedded Signup (TODO quando a branch claude/thirsty-khayyam-9a09d6 for mergeada):
//   O callback /onboarding/whatsapp-callback hoje persiste só o Device
//   (phone_number_id, waba_id). Pra que esta página apareça preenchida
//   automaticamente depois do signup, o callback precisa também atualizar
//   os campos da Company:
//     - waba_display_name  ← business_profile.name (Graph API)
//     - waba_category      ← business_profile.vertical
//     - waba_description   ← business_profile.about / description
//     - meta_bm_id         ← business_id retornado pelo signup
//   razao_social, cnpj e website continuam vindo do cadastro (KYB local),
//   não do signup.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, Divider, Stack, CircularProgress, Chip,
  Tooltip, Tabs, Tab, Container,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiBriefcase, FiCpu, FiFileText,
  FiChevronDown, FiChevronUp, FiCheckCircle, FiInfo,
  FiEdit2, FiTrash2, FiList, FiRefreshCw, FiAlertTriangle, FiLink2,
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import TopBar from '../../components/TopBar';
import TemplateBuilder from './TemplateBuilder';
import TemplateVariableMapDialog from './TemplateVariableMapDialog';
import api from '../../services/Api';
import { emptyForm, templateToForm } from '../../utils/templateMeta';

const SYNE = "'Syne', system-ui, sans-serif";

// Mapa de estilo por status Meta — usado nos chips da aba "Ver e editar templates".
const TEMPLATE_STATUS_STYLES = {
  APPROVED:  { label: 'Aprovado',   bg: '#dcfce7', color: '#16a34a' },
  PENDING:   { label: 'Pendente',   bg: '#fef3c7', color: '#b45309' },
  REJECTED:  { label: 'Rejeitado',  bg: '#fee2e2', color: '#dc2626' },
  PAUSED:    { label: 'Pausado',    bg: '#e2e8f0', color: '#475569' },
  IN_APPEAL: { label: 'Em apelação', bg: '#e0e7ff', color: '#4338ca' },
  DISABLED:  { label: 'Desativado', bg: '#f1f5f9', color: '#64748b' },
};

function statusStyle(status) {
  return TEMPLATE_STATUS_STYLES[String(status || '').toUpperCase()]
    || { label: status || 'Desconhecido', bg: '#f1f5f9', color: '#64748b' };
}

// Resolve URL absoluta para preview (imagens armazenadas no /static do backend).
function resolveImageSrc(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br'}${url}`;
}

// Renderiza preview da formatação WhatsApp (*negrito*, _itálico_, ~tachado~, ```mono```)
function renderWhatsAppPreview(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/```([^`]+)```/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-family:monospace;font-size:0.9em">$1</code>');
  html = html.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');
  html = html.replace(/~([^~\n]+)~/g, '<s>$1</s>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

// Linha label/valor — usada na seção Empresa e nos cards de chip
function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.1 }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, letterSpacing: 0.2 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: value ? '#0f172a' : '#cbd5e1',
          textAlign: 'right',
          maxWidth: '60%',
          wordBreak: 'break-word',
        }}
      >
        {value || '— não informado —'}
      </Typography>
    </Stack>
  );
}

// Card de chip — usado dentro de "Seus Chips Meta"
function ChipCard({ device }) {
  const status = String(device.status || '').toUpperCase();
  const isOnline = status === 'ONLINE';
  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        bgcolor: '#fff',
        p: 2.5,
        transition: 'border-color 0.2s, transform 0.2s',
        '&:hover': { borderColor: '#cbd5e1', transform: 'translateY(-2px)' },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: isOnline ? '#22c55e' : '#94a3b8',
              boxShadow: isOnline ? '0 0 0 4px rgba(34,197,94,0.18)' : 'none',
            }}
          />
          <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', color: '#0f172a', fontFamily: SYNE }}>
            {device.name}
          </Typography>
          {device.phone_number_id && (
            <Tooltip title="Chip Oficial Cloud API verificado pela Meta">
              <Box sx={{ display: 'inline-flex', color: '#3b82f6' }}>
                <FiCheckCircle size={14} />
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Chip
          label={status || 'OFFLINE'}
          size="small"
          sx={{
            bgcolor: isOnline ? '#dcfce7' : '#f1f5f9',
            color: isOnline ? '#16a34a' : '#64748b',
            fontWeight: 700, fontSize: '0.68rem', height: 22, borderRadius: '6px',
          }}
        />
      </Stack>
      <Stack spacing={0.2} divider={<Divider flexItem sx={{ borderColor: '#f1f5f9' }} />}>
        <InfoRow label="Número" value={device.phone_number ? `+${device.phone_number}` : ''} />
        <InfoRow label="Phone Number ID" value={device.phone_number_id} />
        <InfoRow label="WABA ID" value={device.waba_id} />
      </Stack>
    </Box>
  );
}

export default function AdminMeta() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [loading, setLoading] = useState(true);
  const [metaInfo, setMetaInfo] = useState({ company: {}, devices: [] });
  const [msg, setMsg] = useState({ type: '', text: '' });
  // `?aba=templates` abre direto na lista (out/26): é para onde o link
  // "Ver templates" do cartão Administração Meta leva quando há template
  // rejeitado ou pausado.
  const location = useLocation();
  // Lida uma vez, na abertura: o atalho só vale pra quem chegou por ele.
  const consultaInicial = useRef(location.search);
  const [tab, setTab] = useState(() => {
    const aba = new URLSearchParams(location.search).get('aba');
    // `?aba=criar&formulario=…` (Formulários PR 3): o atalho "Criar template"
    // da tela de Formulários abre a criação com o botão já escolhido.
    return aba === 'templates' ? 2 : aba === 'criar' ? 1 : 0;
  });
  const [chipsExpanded, setChipsExpanded] = useState(false);

  // ── Form de template (criação) ──
  // Um objeto só, gerido pelo TemplateBuilder. Antes eram sete useState
  // soltos aqui e outros três no dialog de edição; com botões e exemplos
  // de variável a conta passaria de quinze, e os dois formulários já
  // tinham divergido — era por isso que editar apagava componentes.
  const [tplForm, setTplForm] = useState(() => emptyForm());
  const [submitting, setSubmitting] = useState(false);

  // ── Aba "Ver e editar templates" ──
  const [listDeviceId, setListDeviceId] = useState('');
  const [templatesList, setTemplatesList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Dialog de edição — mesmo formulário da criação, em modo edit.
  const [editingTpl, setEditingTpl] = useState(null); // template object
  const [editForm, setEditForm] = useState(() => emptyForm());
  // Template cujo mapeamento de variáveis está aberto.
  const [mappingTpl, setMappingTpl] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  // Dialog de exclusão
  const [deletingTpl, setDeletingTpl] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const fetchMetaInfo = async () => {
      try {
        const res = await api.get('/templates/meta-info', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMetaInfo(res.data);
        if (res.data.devices?.length > 0) {
          // Pré-seleciona o primeiro chip nas duas abas. No formulário isso
          // vive dentro do próprio estado do form, não num state paralelo.
          setTplForm((current) => ({ ...current, deviceId: res.data.devices[0].id }));
          setListDeviceId(res.data.devices[0].id);
        }
        // Atalho da tela de Formulários: chip do formulário, modo Formulário
        // e um nome sugerido. O resto (texto, categoria) é de quem cria.
        const qs = new URLSearchParams(consultaInicial.current);
        const formularioId = qs.get('formulario');
        if (formularioId) {
          const chip = (res.data.devices || []).find((d) => d.id === qs.get('chip'));
          const sugestao = (qs.get('nome') || '')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
          setTplForm((current) => ({
            ...current,
            ...(chip ? { deviceId: chip.id } : {}),
            name: current.name || sugestao,
            buttonMode: 'FORM', quickReplies: [], ctas: [],
            flowButton: { flowId: formularioId, text: 'Abrir formulário', flowName: qs.get('nome') || '' },
          }));
        }
      } catch (err) {
        setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao carregar dados Meta.' });
      } finally {
        setLoading(false);
      }
    };
    fetchMetaInfo();
  }, [token, navigate]);

  // Upload de imagem — o TemplateBuilder chama nos dois modos e só precisa
  // da URL de volta; quem trata erro de tela é ele.
  const uploadTemplateImage = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post('/templates/upload-image', formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    });
    return res.data.url;
  }, [token]);

  // ─── Listagem de templates (aba 3) ──────────────────────────────────────
  const fetchTemplatesList = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setLoadingList(true);
    setListError('');
    try {
      const res = await api.get(`/templates/list/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplatesList(res.data.templates || []);
    } catch (err) {
      setListError(err.response?.data?.detail || 'Erro ao carregar templates.');
      setTemplatesList([]);
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  // Carrega automaticamente sempre que a aba ficar visível ou trocar o chip.
  useEffect(() => {
    if (tab === 2 && listDeviceId) {
      fetchTemplatesList(listDeviceId);
    }
  }, [tab, listDeviceId, fetchTemplatesList]);

  const filteredTemplates = useMemo(() => {
    if (statusFilter === 'ALL') return templatesList;
    return templatesList.filter((t) => String(t.status).toUpperCase() === statusFilter);
  }, [templatesList, statusFilter]);

  const openEditDialog = (tpl) => {
    setEditingTpl(tpl);
    // Hidrata o formulário inteiro a partir do que a Meta devolveu —
    // inclusive botões e exemplos das variáveis. É isso que permite editar
    // sem redigitar, e sem apagar o que não estava na tela.
    // O chip vai junto (out/26): o botão de formulário lista os formulários
    // publicados DESTE chip, e na edição o chip é o da lista.
    setEditForm({ ...templateToForm(tpl), deviceId: listDeviceId });
    setEditError('');
  };
  const closeEditDialog = () => {
    if (editSubmitting) return;
    setEditingTpl(null);
    setEditError('');
  };

  const handleSaveEdit = async (payload) => {
    if (!editingTpl) return;
    setEditSubmitting(true);
    setEditError('');
    try {
      await api.post(
        `/templates/edit/${listDeviceId}/${editingTpl.id}`,
        {
          ...payload,
          // Template com botão que esta tela não edita (COPY_CODE, Flow):
          // manda `null` em vez de lista vazia. No backend, null quer dizer
          // "não mexi" e preserva os botões atuais; [] os apagaria.
          buttons: editingTpl.has_unsupported_buttons ? null : payload.buttons,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg({ type: 'success', text: `Template "${editingTpl.name}" atualizado. A Meta pode revisar novamente.` });
      setEditingTpl(null);
      fetchTemplatesList(listDeviceId);
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Erro ao editar template.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const openDeleteDialog = (tpl) => {
    setDeletingTpl(tpl);
    setDeleteError('');
  };
  const closeDeleteDialog = () => {
    if (deleteSubmitting) return;
    setDeletingTpl(null);
    setDeleteError('');
  };

  const handleConfirmDelete = async () => {
    if (!deletingTpl) return;
    setDeleteSubmitting(true);
    setDeleteError('');
    try {
      await api.delete(
        `/templates/${listDeviceId}/${encodeURIComponent(deletingTpl.name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMsg({ type: 'success', text: `Template "${deletingTpl.name}" removido.` });
      setDeletingTpl(null);
      fetchTemplatesList(listDeviceId);
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Erro ao remover template.');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // A validação de campo vive no TemplateBuilder (que já bloqueia o botão) e
  // no backend (que é a autoridade). Aqui só resta despachar.
  const handleCreateTemplate = async (payload) => {
    setMsg({ type: '', text: '' });
    setSubmitting(true);
    try {
      const res = await api.post(`/templates/create/${tplForm.deviceId}`, {
        ...payload,
        name: tplForm.name,
        category: tplForm.category,
        language: tplForm.language,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMsg({ type: 'success', text: `${res.data.message} Status atual: ${res.data.status || 'PENDING'}.` });
      // Limpa o conteúdo mas mantém o chip escolhido — quem cria um template
      // normalmente cria vários seguidos no mesmo número.
      setTplForm(emptyForm(tplForm.deviceId));
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao criar template.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <AppSidebar user={user} />
        <Box
          sx={{
            flexGrow: 1,
            ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
            transition: 'margin-left 0.25s ease',
            p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: '#25d366' }} />
        </Box>
      </Box>
    );
  }

  // ─── Derived ──────────────────────────────────────────────────────────────
  const totalDevices = metaInfo.devices.length;
  const onlineDevices = metaInfo.devices.filter(
    (d) => String(d.status || '').toUpperCase() === 'ONLINE'
  ).length;
  const visibleDevices = chipsExpanded ? metaInfo.devices : metaInfo.devices.slice(0, 1);
  const hiddenCount = Math.max(0, totalDevices - 1);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppSidebar user={user} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // `minWidth: 0` não é detalhe: item de flex nasce com
          // `min-width: auto` e por isso NÃO encolhe abaixo da largura
          // mínima do conteúdo. Sem isto, num notebook de 1280px o main
          // media 1180 num espaço de 1024 (a barra lateral come 256) e a
          // página inteira rolava de lado. Mesma correção que Contacts.js
          // já tinha; o Meta ficou de fora na época.
          minWidth: 0,
          ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
          transition: 'margin-left 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* TopBar dark — mesma da página de Conversas/Agentes */}
        <TopBar crumbBase="Administrativo" crumbCur="Meta Business" />

        {/* Área de conteúdo light, rolável */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 3, md: 6 } }}>
          <Container maxWidth="xl" disableGutters>

          {/* ─── HEADER ─── */}
          <Box sx={{ mb: 4 }}>
            {/* Mesmo tamanho do título "Conversas" da página /conversas v2:
                Syne 800, 1.65rem, letter-spacing -0.5px. */}
            <Typography
              sx={{
                fontFamily: SYNE,
                fontWeight: 800,
                fontSize: '1.65rem',
                letterSpacing: '-0.5px',
                lineHeight: 1.1,
                color: '#0f172a',
              }}
            >
              Meta Business
            </Typography>
            <Typography variant="body1" color="#64748b" sx={{ mt: 1 }}>
              Informações da sua conta Meta e criação de templates oficiais.
            </Typography>

            {/* Mini-stats — mesmo estilo das chips em Conversas */}
            <Stack direction="row" spacing={1.2} mt={2} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<FiBriefcase size={12} />}
                label={metaInfo.company.waba_display_name || 'WABA não configurada'}
                size="small"
                sx={{
                  bgcolor: metaInfo.company.waba_display_name ? '#dcfce7' : '#f1f5f9',
                  color: metaInfo.company.waba_display_name ? '#16a34a' : '#94a3b8',
                  fontWeight: 700, fontSize: '0.72rem',
                  '& .MuiChip-icon': {
                    color: metaInfo.company.waba_display_name ? '#16a34a' : '#94a3b8',
                  },
                }}
              />
              <Chip
                icon={<FiCpu size={12} />}
                label={`${totalDevices} chip${totalDevices !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  bgcolor: '#f1f5f9', color: '#475569', fontWeight: 700,
                  fontSize: '0.72rem', '& .MuiChip-icon': { color: '#64748b' },
                }}
              />
              {totalDevices > 0 && (
                <Chip
                  icon={<FiCheckCircle size={12} />}
                  label={`${onlineDevices} online`}
                  size="small"
                  sx={{
                    bgcolor: onlineDevices > 0 ? '#dcfce7' : '#f1f5f9',
                    color: onlineDevices > 0 ? '#16a34a' : '#94a3b8',
                    fontWeight: 700, fontSize: '0.72rem',
                    '& .MuiChip-icon': { color: onlineDevices > 0 ? '#16a34a' : '#94a3b8' },
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Mensagem global */}
          {msg.text && (
            <Alert
              severity={msg.type || 'info'}
              sx={{ mb: 3, borderRadius: 3 }}
              onClose={() => setMsg({ type: '', text: '' })}
            >
              {msg.text}
            </Alert>
          )}

          {/* ─── ABAS ─── */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: '#fff',
            }}
          >
            {/* `scrollable`, não `standard`: os três rótulos somam 676px e o
                scroller do modo standard tem overflow hidden. Num notebook
                estreito ou no celular, "Ver e editar templates" ficava
                cortado e INALCANÇÁVEL — a aba existia e não dava pra
                clicar. Agora a régua rola dentro de si mesma. */}
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                px: { xs: 2, md: 4 },
                borderBottom: '1px solid #f1f5f9',
                '& .MuiTabs-indicator': { backgroundColor: '#25d366', height: 3 },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontFamily: SYNE,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: '#64748b',
                  minHeight: 56,
                  '&.Mui-selected': { color: '#0f172a' },
                },
              }}
            >
              <Tab
                icon={<FiBriefcase size={16} />}
                iconPosition="start"
                label="Dados da empresa"
              />
              <Tab
                icon={<FiFileText size={16} />}
                iconPosition="start"
                label="Criar templates"
              />
              <Tab
                icon={<FiList size={16} />}
                iconPosition="start"
                label="Ver e editar templates"
              />
            </Tabs>

            {/* ─── TAB 1: DADOS DA EMPRESA ─── */}
            {tab === 0 && (
              <Box sx={{ p: { xs: 2.5, md: 4 } }}>

                {/* Aviso de read-only */}
                <Alert
                  severity="info"
                  icon={<FiInfo size={18} />}
                  sx={{
                    mb: 3, borderRadius: 3,
                    bgcolor: '#eff6ff', color: '#1e40af',
                    border: '1px solid #bfdbfe',
                    '& .MuiAlert-icon': { color: '#2563eb' },
                  }}
                >
                  Estes dados vêm direto da sua conta Meta Business e não podem ser editados aqui.
                  Se alguma informação estiver incorreta, ajuste no seu Meta Business Manager ou{' '}
                  <Box
                    component="a"
                    href="https://wa.me/5521997759098?text=Olá, preciso corrigir os dados da minha conta Meta na Cetto"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#1d4ed8', fontWeight: 700, textDecoration: 'underline',
                      '&:hover': { color: '#1e3a8a' },
                    }}
                  >
                    fale com a nossa equipe
                  </Box>.
                </Alert>

                {/* Empresa na Meta */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3, border: '1px solid #e2e8f0',
                    overflow: 'hidden', mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      px: 3, py: 2.5,
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        bgcolor: '#f0fdf4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <FiBriefcase size={18} color="#16a34a" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontFamily: SYNE, fontWeight: 800, color: '#0f172a', fontSize: '1.05rem' }}
                      >
                        Empresa na Meta
                      </Typography>
                      <Typography variant="caption" color="#94a3b8">
                        Dados cadastrais e perfil do WhatsApp Business
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: 3, py: 1.5 }}>
                    <Stack divider={<Divider flexItem sx={{ borderColor: '#f1f5f9' }} />}>
                      <InfoRow label="Razão Social" value={metaInfo.company.razao_social} />
                      <InfoRow label="CNPJ" value={metaInfo.company.cnpj} />
                      <InfoRow label="Website" value={metaInfo.company.website} />
                      <InfoRow label="Meta Business Manager ID" value={metaInfo.company.meta_bm_id} />
                      <InfoRow label="Nome de Exibição (WABA)" value={metaInfo.company.waba_display_name} />
                      <InfoRow label="Categoria WABA" value={metaInfo.company.waba_category} />
                      <InfoRow label="Descrição WABA" value={metaInfo.company.waba_description} />
                    </Stack>
                  </Box>
                </Paper>

                {/* Seus Chips Meta — só 1 visível por default */}
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3, border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 3, py: 2.5,
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        bgcolor: '#f0fdf4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <FiCpu size={18} color="#16a34a" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{ fontFamily: SYNE, fontWeight: 800, color: '#0f172a', fontSize: '1.05rem' }}
                      >
                        Seus Chips Meta
                      </Typography>
                      <Typography variant="caption" color="#94a3b8">
                        WABA e identificadores de cada chip da empresa
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    {totalDevices === 0 ? (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <FiCpu size={36} color="#cbd5e1" />
                        <Typography color="#94a3b8" mt={1.5} fontWeight={600}>
                          Nenhum chip cadastrado.
                        </Typography>
                        <Typography variant="caption" color="#cbd5e1">
                          Conecte um chip oficial pelo Dashboard.
                        </Typography>
                      </Box>
                    ) : (
                      <Stack spacing={2}>
                        {visibleDevices.map((d) => (
                          <ChipCard key={d.id} device={d} />
                        ))}
                        {hiddenCount > 0 && (
                          <Button
                            onClick={() => setChipsExpanded((v) => !v)}
                            endIcon={
                              chipsExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />
                            }
                            sx={{
                              alignSelf: 'center',
                              textTransform: 'none',
                              fontFamily: SYNE,
                              fontWeight: 700,
                              color: '#16a34a',
                              borderRadius: 2,
                              px: 2,
                              '&:hover': { bgcolor: '#f0fdf4' },
                            }}
                          >
                            {chipsExpanded
                              ? 'Mostrar menos'
                              : `Ver mais (${hiddenCount} chip${hiddenCount !== 1 ? 's' : ''})`}
                          </Button>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}

            {/* ─── ABA 2: CRIAR TEMPLATE ─── */}
            {tab === 1 && (
              <TemplateBuilder
                mode="create"
                form={tplForm}
                onChange={setTplForm}
                devices={metaInfo.devices}
                onSubmit={handleCreateTemplate}
                submitting={submitting}
                onUploadImage={uploadTemplateImage}
                imageResolver={resolveImageSrc}
              />
            )}

            {/* ─── TAB 3: VER E EDITAR TEMPLATES ─── */}
            {tab === 2 && (
              <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                {/* Header da aba: seletor de chip, filtro e refresh */}
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ md: 'center' }}
                  mb={3}
                >
                  <TextField
                    select
                    label="Chip (WABA)"
                    value={listDeviceId}
                    onChange={(e) => setListDeviceId(e.target.value)}
                    size="small"
                    sx={{ minWidth: 260 }}
                  >
                    {metaInfo.devices.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name} — {d.phone_number}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="small"
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="ALL">Todos</MenuItem>
                    <MenuItem value="APPROVED">Aprovados</MenuItem>
                    <MenuItem value="PENDING">Pendentes</MenuItem>
                    <MenuItem value="REJECTED">Rejeitados</MenuItem>
                    <MenuItem value="PAUSED">Pausados</MenuItem>
                    <MenuItem value="IN_APPEAL">Em apelação</MenuItem>
                    <MenuItem value="DISABLED">Desativados</MenuItem>
                  </TextField>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    onClick={() => fetchTemplatesList(listDeviceId)}
                    disabled={loadingList || !listDeviceId}
                    startIcon={
                      loadingList
                        ? <CircularProgress size={14} sx={{ color: '#16a34a' }} />
                        : <FiRefreshCw size={14} />
                    }
                    sx={{
                      textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                      color: '#16a34a', borderRadius: 2,
                      '&:hover': { bgcolor: '#f0fdf4' },
                    }}
                  >
                    Atualizar
                  </Button>
                </Stack>

                {listError && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }} onClose={() => setListError('')}>
                    {listError}
                  </Alert>
                )}

                {loadingList ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: '#25d366' }} />
                  </Box>
                ) : filteredTemplates.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <FiFileText size={36} color="#cbd5e1" />
                    <Typography color="#94a3b8" mt={1.5} fontWeight={600} fontFamily={SYNE}>
                      {templatesList.length === 0
                        ? 'Nenhum template encontrado neste chip.'
                        : 'Nenhum template com esse status.'}
                    </Typography>
                    <Typography variant="caption" color="#cbd5e1">
                      {templatesList.length === 0
                        ? 'Crie um template na aba "Criar templates" para começar.'
                        : 'Ajuste o filtro de status acima.'}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {filteredTemplates.map((tpl) => {
                      const st = statusStyle(tpl.status);
                      return (
                        <Paper
                          key={tpl.id || tpl.name}
                          elevation={0}
                          sx={{
                            border: '1px solid #e2e8f0',
                            borderRadius: 3,
                            p: 2.5,
                            transition: 'border-color 0.2s, transform 0.2s',
                            '&:hover': { borderColor: '#cbd5e1', transform: 'translateY(-2px)' },
                          }}
                        >
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            {tpl.image_url && (
                              <Box
                                component="img"
                                src={resolveImageSrc(tpl.image_url)}
                                sx={{
                                  width: { xs: '100%', md: 96 },
                                  height: { xs: 160, md: 96 },
                                  borderRadius: 2, objectFit: 'cover',
                                  flexShrink: 0,
                                }}
                                alt={tpl.name}
                              />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap mb={0.5}>
                                <Typography
                                  sx={{
                                    fontFamily: SYNE, fontWeight: 800, fontSize: '1rem',
                                    color: '#0f172a', wordBreak: 'break-all',
                                  }}
                                >
                                  {tpl.label || tpl.name}
                                </Typography>
                                <Chip
                                  label={st.label}
                                  size="small"
                                  sx={{
                                    bgcolor: st.bg, color: st.color,
                                    fontWeight: 700, fontSize: '0.68rem', height: 22, borderRadius: '6px',
                                  }}
                                />
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1}>
                                <Chip
                                  label={tpl.category || '—'}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f1f5f9', color: '#475569',
                                    fontWeight: 700, fontSize: '0.66rem', height: 20, borderRadius: '6px',
                                  }}
                                />
                                <Chip
                                  label={tpl.language || '—'}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f1f5f9', color: '#475569',
                                    fontWeight: 700, fontSize: '0.66rem', height: 20, borderRadius: '6px',
                                  }}
                                />
                              </Stack>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#475569', fontSize: '0.85rem',
                                  lineHeight: 1.5, mb: 1,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: renderWhatsAppPreview(tpl.body || '(sem corpo)'),
                                }}
                              />
                              {tpl.footer && (
                                <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mb: 1 }}>
                                  Rodapé: {tpl.footer}
                                </Typography>
                              )}
                            </Box>
                            <Stack
                              direction={{ xs: 'row', md: 'column' }}
                              spacing={1}
                              sx={{ flexShrink: 0, alignSelf: { md: 'flex-start' } }}
                            >
                              {/* Só aparece quando o template tem {{n}}:
                                  num template sem variável o botão levaria a
                                  um diálogo que só diz "nada a mapear". */}
                              {/\{\{\s*\d+\s*\}\}/.test(tpl.body || '') && (
                                <Button
                                  onClick={() => setMappingTpl(tpl)}
                                  startIcon={<FiLink2 size={14} />}
                                  sx={{
                                    textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                                    color: '#0f172a', borderRadius: 2,
                                    border: '1px solid #e2e8f0',
                                    px: 2,
                                    '&:hover': { borderColor: '#25d366', bgcolor: '#f0fdf4', color: '#16a34a' },
                                  }}
                                >
                                  Variáveis
                                </Button>
                              )}
                              <Button
                                onClick={() => openEditDialog(tpl)}
                                startIcon={<FiEdit2 size={14} />}
                                sx={{
                                  textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                                  color: '#0f172a', borderRadius: 2,
                                  border: '1px solid #e2e8f0',
                                  px: 2,
                                  '&:hover': { borderColor: '#25d366', bgcolor: '#f0fdf4', color: '#16a34a' },
                                }}
                              >
                                Editar
                              </Button>
                              <Button
                                onClick={() => openDeleteDialog(tpl)}
                                startIcon={<FiTrash2 size={14} />}
                                sx={{
                                  textTransform: 'none', fontFamily: SYNE, fontWeight: 700,
                                  color: '#dc2626', borderRadius: 2,
                                  border: '1px solid #fecaca',
                                  px: 2,
                                  '&:hover': { borderColor: '#dc2626', bgcolor: '#fef2f2' },
                                }}
                              >
                                Excluir
                              </Button>
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            )}
          </Paper>
          </Container>
        </Box>
      </Box>

      {/* ─── DIALOG DE EDIÇÃO ─── */}
      <Dialog
        open={Boolean(editingTpl)}
        onClose={closeEditDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle
          sx={{
            fontFamily: SYNE, fontWeight: 800, color: '#0f172a',
            borderBottom: '1px solid #f1f5f9', pb: 2,
          }}
        >
          Editar template
          {editingTpl && (
            <Typography variant="caption" color="#94a3b8" display="block" sx={{ fontWeight: 500 }}>
              {editingTpl.name} · {editingTpl.language} · {editingTpl.category}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Alert
            severity="info"
            icon={<FiInfo size={16} />}
            sx={{ m: 3, mb: 0, borderRadius: 3, fontSize: '0.8rem' }}
          >
            Nome, categoria e idioma são imutáveis depois do envio. Templates
            aprovados só podem ser editados 1x a cada 24h — a contagem é da Meta.
          </Alert>

          {/* Mesmo formulário da criação. Não é reaproveitamento por economia:
              quando eram dois formulários, o de edição mandava só corpo,
              rodapé e imagem, e como a Meta substitui o conjunto inteiro de
              componentes, todo o resto era apagado em silêncio. */}
          <TemplateBuilder
            mode="edit"
            form={editForm}
            onChange={setEditForm}
            onSubmit={handleSaveEdit}
            submitting={editSubmitting}
            error={editError}
            onUploadImage={uploadTemplateImage}
            imageResolver={resolveImageSrc}
            lockedButtons={Boolean(editingTpl?.has_unsupported_buttons)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button
            onClick={closeEditDialog}
            disabled={editSubmitting}
            sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: '#64748b' }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── DIALOG DE MAPEAMENTO DE VARIÁVEIS ─── */}
      <TemplateVariableMapDialog
        open={Boolean(mappingTpl)}
        template={mappingTpl}
        deviceId={listDeviceId}
        onClose={() => setMappingTpl(null)}
        onSaved={() => fetchTemplatesList(listDeviceId)}
      />

      {/* ─── DIALOG DE EXCLUSÃO ─── */}
      <Dialog
        open={Boolean(deletingTpl)}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle
          sx={{
            fontFamily: SYNE, fontWeight: 800, color: '#0f172a',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '10px',
              bgcolor: '#fef2f2', color: '#dc2626',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FiAlertTriangle size={18} />
          </Box>
          Excluir template
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#475569" sx={{ mb: 1.5 }}>
            Tem certeza que deseja excluir o template{' '}
            <strong style={{ color: '#0f172a' }}>{deletingTpl?.name}</strong>?
          </Typography>
          <Typography variant="caption" color="#94a3b8">
            Esta ação é irreversível. O template será removido do WhatsApp Business
            e não poderá ser recuperado — precisará ser recriado do zero.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeDeleteDialog}
            disabled={deleteSubmitting}
            sx={{ textTransform: 'none', fontFamily: SYNE, fontWeight: 700, color: '#64748b' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteSubmitting}
            variant="contained"
            startIcon={deleteSubmitting ? <CircularProgress size={14} color="inherit" /> : <FiTrash2 size={14} />}
            sx={{
              bgcolor: '#dc2626', color: '#fff', fontWeight: 700,
              fontFamily: SYNE, textTransform: 'none', borderRadius: 2,
              '&:hover': { bgcolor: '#b91c1c' },
              '&.Mui-disabled': { bgcolor: '#fecaca', color: '#fff' },
            }}
          >
            {deleteSubmitting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
